// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/QuestionForm.test.tsx
import { Button, Flex, Text } from "@radix-ui/themes";
import cx from "classnames";
import { type ReactNode } from "react";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type QuestionFormCardAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Suffix after `{testIdPrefix}-action-`. Submit keeps `question-submit-btn`. */
  action: string;
  /** Override the generated action testid (ReviewPreflight start / change-scope). */
  testId?: string;
};

export type QuestionFormCardProps = {
  title?: string;
  detail1?: string;
  detail2?: string;
  detail3?: string;
  children?: ReactNode;
  primary?: QuestionFormCardAction;
  secondary?: QuestionFormCardAction;
  tertiary?: QuestionFormCardAction;
  /** Outer card testid. Product defaults to `question-card`. */
  testId?: string;
  /**
   * Prefix for title / details / slot / action children.
   * why: SLOP-T-15 — every repeated child must be prefixed, not only the wrapper.
   */
  testIdPrefix?: string;
  questionIndex?: number;
  className?: string;
  /**
   * `shell` = Figma QuestionForm (accent border + soft shadow, title outside body).
   * `inner` = nested body only (ReviewPreflight confirm/scope slot).
   * `plain` = no card chrome — parent (composer) owns the accent shell.
   */
  chrome?: "shell" | "inner" | "plain";
};

function actionTestId(prefix: string, action: QuestionFormCardAction): string {
  if (action.testId) return action.testId;
  if (action.action === "submit") return "question-submit-btn";
  return `${prefix}-action-${action.action}`;
}

/** Figma QuestionForm 49620:414 outer — accent-7 ring + soft shadow-3. */
export const QUESTION_FORM_SHELL_CLASS =
  "rounded-lg border border-puntt-accent-7 p-2 shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02),0_4px_16px_rgba(23,26,36,0.02)]";

/**
 * Body / nested slot chrome — same fill as the parent (no inset gray box).
 * why: Figma QuestionForm body is transparent over the shell/composer surface.
 */
export const QUESTION_FORM_INNER_CLASS =
  "flex w-full min-w-0 flex-col gap-4 px-3 py-2";

/**
 * Optional Title / Details / Slot / Action Row matching Figma QuestionForm.
 * why: title sits outside the body; actions stay left size-1 and vertically centered.
 */
export function QuestionFormCard({
  title,
  detail1,
  detail2,
  detail3,
  children,
  primary,
  secondary,
  tertiary,
  testId = "question-card",
  testIdPrefix = "question-form",
  questionIndex,
  className,
  chrome = "shell",
}: QuestionFormCardProps) {
  const hasDetails = Boolean(detail1 || detail2 || detail3);
  const hasSlot = children != null;
  const hasActions = Boolean(primary || secondary || tertiary);
  const hasBody = hasDetails || hasSlot || hasActions;
  const innerOnly = chrome === "inner";
  const plain = chrome === "plain";
  const flattenChrome = innerOnly || plain;

  const details = hasDetails ? (
    <div
      data-testid={`${testIdPrefix}-details`}
      className="flex flex-col gap-1"
    >
      {detail1 ? (
        <Text
          as="p"
          size="3"
          weight="medium"
          data-testid={`${testIdPrefix}-detail-1`}
          className="text-puntt-neutral-gray-12"
        >
          {detail1}
        </Text>
      ) : null}
      {detail2 ? (
        <Text
          as="p"
          size="1"
          data-testid={`${testIdPrefix}-detail-2`}
          className="text-puntt-neutral-gray-11"
        >
          {detail2}
        </Text>
      ) : null}
      {detail3 ? (
        <Text
          as="p"
          size="1"
          data-testid={`${testIdPrefix}-detail-3`}
          className="text-puntt-neutral-gray-11"
        >
          {detail3}
        </Text>
      ) : null}
    </div>
  ) : null;

  const slot = hasSlot ? (
    <div data-testid={`${testIdPrefix}-slot`} className="min-w-0">
      {children}
    </div>
  ) : null;

  const actions = hasActions ? (
    <Flex
      gap="2"
      wrap="wrap"
      justify="start"
      align="center"
      data-testid={`${testIdPrefix}-action-row`}
      className="items-center"
    >
      {primary ? (
        <Button
          data-testid={actionTestId(testIdPrefix, primary)}
          size="1"
          variant="solid"
          color="blue"
          disabled={primary.disabled}
          onClick={primary.onClick}
          className="h-6"
        >
          {primary.label}
        </Button>
      ) : null}
      {secondary ? (
        <Button
          data-testid={actionTestId(testIdPrefix, secondary)}
          size="1"
          variant="outline"
          disabled={secondary.disabled}
          onClick={secondary.onClick}
          className="h-6"
        >
          {secondary.label}
        </Button>
      ) : null}
      {tertiary ? (
        <Button
          data-testid={actionTestId(testIdPrefix, tertiary)}
          size="1"
          variant="ghost"
          disabled={tertiary.disabled}
          onClick={tertiary.onClick}
          className="h-6"
        >
          {tertiary.label}
        </Button>
      ) : null}
    </Flex>
  ) : null;

  const body = hasBody ? (
    <div
      data-testid={`${testIdPrefix}-body`}
      className={QUESTION_FORM_INNER_CLASS}
    >
      {details}
      {slot}
      {actions}
    </div>
  ) : null;

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.questionFormCard)}
      data-testid={testId}
      data-question-index={questionIndex}
      data-chrome={chrome}
      className={cx(
        chrome === "shell" && QUESTION_FORM_SHELL_CLASS,
        flattenChrome && QUESTION_FORM_INNER_CLASS,
        chrome === "shell" && "flex flex-col gap-2",
        flattenChrome && (hasSlot ? "gap-4" : "gap-2"),
        className,
      )}
    >
      {title ? (
        <Text
          as="p"
          size="4"
          weight="medium"
          data-testid={`${testIdPrefix}-title`}
          className={cx(
            "text-puntt-neutral-gray-12",
            chrome === "shell" && "px-2",
          )}
        >
          {title}
        </Text>
      ) : null}
      {flattenChrome ? (
        <>
          {details}
          {slot}
          {actions}
        </>
      ) : (
        body
      )}
    </div>
  );
}
